// The GIF picker's grid: which previews load, in what order, and which picture fills a cell (#736). One rule for every
// client; the cases are shared/cases/gif-grid.json, which the Swift and Kotlin ports run too.
//
// A cell is its position in the results (0 is the first KLIPY gave; their order is kept). A client tells this rule two
// things about the cells it has: which are `visible` (any part on screen) and which are `near` (inside the look-ahead
// zone, about one screen past the edges; the visible cells may be in this list too). The rule answers which cells may
// start loading and which downloads to cancel. Nothing outside `visible` and `near` loads.

/** How many previews download at once. A few at a time keep the cells on screen from sharing the link with the rest. */
export const GIF_LOAD_LIMIT = 8;

/**
 * The order previews load in: the visible cells top to bottom, then the near cells below the screen, nearest first, then
 * the near cells above it, nearest first. Each cell once.
 */
export function gifLoadOrder(visible: readonly number[], near: readonly number[]): number[] {
  const seen = new Set<number>();
  const out: number[] = [];
  const add = (i: number) => {
    if (!seen.has(i)) {
      seen.add(i);
      out.push(i);
    }
  };
  const on = [...new Set(visible)].sort((x, y) => x - y);
  for (const i of on) add(i);
  const rest = [...new Set(near)].filter((i) => !seen.has(i));
  const last = on.length ? on[on.length - 1]! : -1;
  for (const i of rest.filter((n) => n > last).sort((x, y) => x - y)) add(i);
  for (const i of rest.filter((n) => n < last).sort((x, y) => y - x)) add(i);
  return out;
}

export interface GifLoadState {
  visible: readonly number[];
  near: readonly number[];
  /** Cells whose picture has arrived. They stay as they are. */
  loaded: readonly number[];
  /** Cells that started and have not finished. */
  inFlight: readonly number[];
  /** How many downloads may run at once (`GIF_LOAD_LIMIT`). */
  limit: number;
}

export interface GifLoadStep {
  /** Cells to start now, first first. */
  start: number[];
  /** Cells whose download to stop: they left the zone, or a cell on screen needs the place. */
  cancel: number[];
}

/**
 * One step of the loader: call it after anything changes (a cell comes into or leaves a zone, a picture arrives or fails).
 * - A download for a cell outside both zones is cancelled.
 * - Free places go to the waiting cells in `gifLoadOrder`, but a near cell starts only when every visible cell has its
 *   picture: what is on screen loads before anything that is not.
 * - A visible cell never waits behind a near one: when no place is free, the near downloads that started last are
 *   cancelled for it.
 */
export function gifLoadStep(s: GifLoadState): GifLoadStep {
  const loaded = new Set(s.loaded);
  const wanted = gifLoadOrder(s.visible, s.near).filter((i) => !loaded.has(i));
  const wantedSet = new Set(wanted);
  const visible = new Set(s.visible);
  const cancel = new Set(s.inFlight.filter((i) => !wantedSet.has(i)));
  const running = s.inFlight.filter((i) => wantedSet.has(i));
  const runningSet = new Set(running);
  const waiting = wanted.filter((i) => !runningSet.has(i));
  let free = Math.max(0, s.limit - running.length);
  const need = Math.min(waiting.filter((i) => visible.has(i)).length, s.limit) - free;
  if (need > 0) {
    // The near downloads that rank last in the order go first.
    const victims = running.filter((i) => !visible.has(i)).sort((x, y) => wanted.indexOf(y) - wanted.indexOf(x));
    for (const v of victims.slice(0, need)) {
      cancel.add(v);
      free++;
    }
  }
  // The look-ahead waits for the screen: while a visible cell has no picture yet, only visible cells start.
  const screenPending = s.visible.some((i) => !loaded.has(i));
  const startable = screenPending ? waiting.filter((i) => visible.has(i)) : waiting;
  return { start: startable.slice(0, free), cancel: [...cancel].sort((x, y) => x - y) };
}

/** The picture size, in device pixels, a cell needs along one side: its size in CSS pixels times the screen's scale. */
export function gifNeededPixels(cellWidth: number, scale: number): number {
  return Math.ceil(Math.max(0, cellWidth) * Math.max(1, scale));
}

/**
 * The smallest picture that covers the cell: of the candidates, the narrowest one at least `neededWidth` wide and at least
 * `neededHeight` high (a cell that crops with `object-fit: cover` needs both; pass 0 for a height that follows the width),
 * or the widest when none covers. Returns the index in `sizes`, or -1 for no candidates.
 */
export function gifPickRendition(sizes: readonly { width: number; height: number }[], neededWidth: number, neededHeight = 0): number {
  let best = -1;
  let wide = -1;
  sizes.forEach((s, i) => {
    if (wide < 0 || s.width > sizes[wide]!.width) wide = i;
    if (s.width >= neededWidth && s.height >= neededHeight && (best < 0 || s.width < sizes[best]!.width)) best = i;
  });
  return best >= 0 ? best : wide;
}
